// The history module (#3382 milestone 1): the operations that write a conversation's view,
// each through the lease it is handed (A-4c). No instance state.
import { isSystemErrorText } from "../system-error-text.js";
import { toolCardIndexOf } from "../task-notification-prompt.js";
import type { AgentStream, StreamEntry } from "./types.js";
import type { AgentTimelineItem } from "../types.js";
import type { ConversationWrite } from "./conversation-history.js";
import {
  bodyOfChunks,
  chunkPartsOf,
  chunksOf,
  highestSeq,
  rowsFollowEachOther,
  rowsWith,
  seqRangeOf,
  seqsOf,
  takeRowIntoLedger,
  takeRowIntoLedgerBody,
  withTextBody,
} from "./rows.js";

// Entries a stream keeps before the cap trims its head (AgentStreamState.MAX_ENTRIES).
export const VIEW_MAX_ENTRIES = 1000;

// What a view operation may do with its lease: assign the view it was handed.
type Assign = Pick<ConversationWrite, "assign">;

// Where a row created at `ts` belongs by real time: the index of the first strictly newer
// entry, or -1 (append). An undated entry keeps the dated one before it, so a turn boundary
// never drifts into the turn it closed; why not a sort: knowledge/124-sync/history-module.md.
export function insertIndexForTs(s: AgentStream, ts: number | undefined): number {
  if (ts === undefined) return -1;
  return s.entries.findIndex((e) => e.ts !== undefined && e.ts > ts);
}

// An entry carries the HIGHEST durable row it covers. The row's entry is whatever the
// replay CHANGED — appended, merged into, or updated in place (a tool completing behind
// a reply, #3248 R2) — told by identity against the list before it. A row that changed
// nothing (a filtered scaffold, a no-op echo) is still covered by the last entry, or
// rowsBelowPageFloor's contiguity test would read the gap as a hole.
export function stampCommittedSeq(
  w: Assign,
  s: AgentStream,
  seq: number,
  prev: readonly StreamEntry[],
): void {
  if (s.entries.length === 0) return;
  const was = new Map(prev.map((entry) => [entry.id, entry]));
  let stamped = false;
  const next = s.entries.map((entry) => {
    if (was.get(entry.id) === entry) return entry;
    stamped = true;
    return { ...entry, seq: highestSeq(entry.seq, seq) };
  });
  if (stamped) {
    w.assign(s, next);
    return;
  }
  const last = s.entries.length - 1;
  w.assign(
    s,
    s.entries.map((entry, index) => {
      if (index !== last) return entry;
      // A zero-length part keeps the chunk chain contiguous across a row the screen
      // does not draw, so the next chunk still reads as this message's continuation.
      if (entry.kind === "text" || entry.kind === "thinking") {
        return { ...entry, seq, parts: [...chunkPartsOf(entry), { seq, len: 0 }] };
      }
      return { ...entry, seq: highestSeq(entry.seq, seq), rows: rowsWith(entry, seq) };
    }),
  );
}

export function rebuildApplied(s: AgentStream): void {
  s.applied = new Set();
  for (const e of s.entries) for (const seq of seqsOf(e)) s.applied.add(seq);
}

// A row next to a reply repaired from the DM ledger goes into that reply by the ledger
// rule (takeRowIntoLedger, #3238), never beside it: a ledger has no row seqs to union by.
export function takeRowIntoLedgerText(
  w: Assign,
  s: AgentStream,
  item: AgentTimelineItem,
  seq: number,
): boolean {
  if (item.type !== "assistant_message" || !item.messageId || isSystemErrorText(item.text)) {
    return false;
  }
  for (let i = 0; i < s.entries.length; i += 1) {
    const e = s.entries[i];
    if (e.kind !== "text" || e.messageId !== item.messageId) continue;
    // An entry with no rows at all is a ledger row drawn before its timeline (Fab's DM,
    // 2026-10-14): the message's rows take its text over, wherever they land in time.
    const drawnFromLedger = !e.parts?.length && e.seq === undefined;
    const chunks = drawnFromLedger
      ? takeRowIntoLedgerBody(chunksOf(e), { seq, text: item.text })
      : takeRowIntoLedger(chunksOf(e), { seq, text: item.text });
    if (!chunks) continue;
    const taken = withTextBody(e, bodyOfChunks(chunks));
    w.assign(
      s,
      s.entries.map((x, j) => (j === i ? taken : x)),
    );
    return true;
  }
  return false;
}

// Two compaction cards that are NEIGHBOURS and cover consecutive rows are one run: a
// separate compaction always has the turn it followed in between, so it is never
// adjacent. Repairs a run a page boundary split — its lower rows land outside
// compactionCardFor's ±1 window and open a second card until this closes the gap.
// ponytail: one pair per call; a row extends a card by one, so one gap can close.
export function fuseCompactionRun(w: Assign, s: AgentStream): void {
  const i = s.entries.findIndex(
    (e, k) =>
      e.kind === "compaction" &&
      s.entries[k + 1]?.kind === "compaction" &&
      rowsFollowEachOther(e, s.entries[k + 1]),
  );
  if (i < 0) return;
  const a = s.entries[i] as StreamEntry & { kind: "compaction" };
  const b = s.entries[i + 1] as StreamEntry & { kind: "compaction" };
  const rows = [...new Set([...seqsOf(a), ...seqsOf(b)])].sort((x, y) => x - y);
  w.assign(s, [
    ...s.entries.slice(0, i),
    {
      id: a.id,
      kind: "compaction",
      status: a.status === "completed" || b.status === "completed" ? "completed" : "loading",
      rows,
      seq: rows[rows.length - 1],
    },
    ...s.entries.slice(i + 2),
  ]);
}

export function compactionCardFor(s: AgentStream, seq: number | undefined): number {
  if (seq !== undefined) {
    return s.entries.findIndex(
      (e) => e.kind === "compaction" && seqsOf(e).some((r) => Math.abs(r - seq) <= 1),
    );
  }
  for (let i = s.entries.length - 1; i >= 0; i--) {
    const e = s.entries[i];
    if (e.kind === "turn_boundary") return -1;
    if (e.kind === "compaction" && e.status === "loading") return i;
  }
  return -1;
}

// Whether a row below the screen's floor is the head the screen's first text or
// thinking entry continues from: that entry's lowest seq is the row right after it.
export function headChainsTo(s: AgentStream, seq: number): boolean {
  const first = s.entries.find((e) => seqRangeOf(e) !== undefined);
  const range = first ? seqRangeOf(first) : undefined;
  return !!first && (first.kind === "text" || first.kind === "thinking") && range?.min === seq + 1;
}

// Where a row at `seq` sits: after the last entry below it. A row newer than all on
// screen (or with no seq) is live and sits at the end, past sends with no seq yet.
export function rowPosition(s: AgentStream, seq: number | undefined): number {
  if (seq === undefined || !s.entries.some((e) => (seqRangeOf(e)?.max ?? -1) > seq)) {
    return s.entries.length;
  }
  let at = 0;
  for (let i = 0; i < s.entries.length; i += 1) {
    const range = seqRangeOf(s.entries[i]);
    if (range && range.max < seq) at = i + 1;
  }
  return at;
}

// The row's turn, nearest first; a trailing divider (turn_completed) is stepped over.
export function turnWindow(
  s: AgentStream,
  seq: number | undefined,
  stepOverClosed = true,
): number[] {
  const at = rowPosition(s, seq);
  const out: number[] = [];
  for (let i = at - 1; i >= 0; i -= 1) {
    if (s.entries[i].kind === "turn_boundary") {
      if (stepOverClosed && i === s.entries.length - 1 && at === s.entries.length) continue;
      break;
    }
    out.push(i);
  }
  return out;
}

export function updatesEntityOnScreen(
  s: AgentStream,
  item: AgentTimelineItem,
  seq: number,
): boolean {
  if (item.type === "tool_call") {
    return toolCardIndexOf(s.entries, item) >= 0;
  }
  if (item.type === "compaction") return compactionCardFor(s, seq) >= 0;
  return false;
}

// Where a TIMED entry belongs: at its `ts`, but never above an entry that carries no
// time at all. An untimed entry — a turn boundary, a tool card, a chunk that streamed
// before its row was committed — has no place in a time order, so it fences the search
// instead of being stepped over. That fence is also why a fresh send still lands at the
// end of a live turn when the daemon's clock runs ahead of ours. -1 means "append".
export function seatByTs(s: AgentStream, ts: number | undefined): number {
  if (ts === undefined) return -1;
  // Above the cap the trim would drop exactly the head this seats into, and the caller
  // holds the entry's id to settle its receipt against. An append is never trimmed.
  // Against what the trim WILL keep: a paged-back conversation sits above MAX_ENTRIES
  // on purpose, and its head is not going anywhere, so it still seats by time.
  if (s.entries.length >= Math.max(VIEW_MAX_ENTRIES, s.keepFloor)) return -1;
  let floor = 0;
  for (let i = s.entries.length - 1; i >= 0; i -= 1) {
    if (s.entries[i].ts === undefined) {
      floor = i + 1;
      break;
    }
  }
  for (let i = floor; i < s.entries.length; i += 1) {
    const at = s.entries[i].ts;
    if (at !== undefined && at > ts) return i;
  }
  return -1;
}
